import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { BOARD_WRITES } from '@ludus/core'
import type { BoardWrite, PipelineId } from '@ludus/core'
import { fetchPipelineAdapter, type PipelineAdapter } from '@/lib/api/pipelines.ts'

export interface PipelineAdapterState {
  /** `null` while loading and when the adapter could not be asked. */
  adapter: PipelineAdapter | null
  isLoading: boolean
}

export function usePipelineAdapter(pipelineId: PipelineId | null): PipelineAdapterState {
  const { data, isLoading } = useSWR(
    pipelineId === null ? null : `/pipelines/${pipelineId}/adapter`,
    () => fetchPipelineAdapter(pipelineId as PipelineId),
  )
  return { adapter: data ?? null, isLoading }
}

export function boardUnreachableMessage(t: TFunction): string {
  return t('pages.pipelines.writes.unreachable', {
    defaultValue: 'The source could not be reached, so this board is read-only for now.',
  })
}

export function writeDeniedMessage(t: TFunction, write: BoardWrite, source: string): string {
  switch (write) {
    case 'create':
      return t('pages.pipelines.writes.create', {
        defaultValue: 'Deals on this board are created in {{source}}.',
        source,
      })
    case 'moveStage':
      return t('pages.pipelines.writes.stage', {
        defaultValue: 'Stages on this board are set in {{source}}.',
        source,
      })
    case 'reassign':
      return t('pages.pipelines.writes.owner', {
        defaultValue: 'Owners on this board are set in {{source}}.',
        source,
      })
    default:
      return t('pages.pipelines.writes.fields', {
        defaultValue: 'Card details on this board are set in {{source}}.',
        source,
      })
  }
}

export function refusedWriteMessage(
  t: TFunction,
  err: { kind: string; write?: BoardWrite | null; source?: string | null },
): string | null {
  if (err.kind === 'source_unavailable') return boardUnreachableMessage(t)
  if (err.kind === 'write_not_carried' && err.write && err.source) {
    return writeDeniedMessage(t, err.write, err.source)
  }
  return null
}

/**
 * Why each write is refused, or `null` where the board takes it. A mirrored
 * board takes only what its adapter can carry back to the source; the rest
 * would land in `tenant.sqlite` and silently diverge on the next sync (#65).
 * A disconnected board has no source to diverge from and takes everything.
 */
export function useDeniedWrites(pipelineId: PipelineId | null): Record<BoardWrite, string | null> {
  const { t } = useTranslation()
  const { adapter } = usePipelineAdapter(pipelineId)
  const each = (reason: (write: BoardWrite) => string | null) =>
    Object.fromEntries(BOARD_WRITES.map((w) => [w, reason(w)])) as Record<BoardWrite, string | null>
  if (adapter === null) return each(() => boardUnreachableMessage(t))
  if (adapter.adapterKind === null) return each(() => null)
  const source = adapter.adapterKind
  // Only a stage move and an owner change have a write in the contract; create
  // and the card's fields have none, so a connected board never takes them.
  const carried = (write: BoardWrite) =>
    (write === 'moveStage' && adapter.capabilities.writeStatus) ||
    (write === 'reassign' && adapter.capabilities.writeAssignment)
  return each((write) => (carried(write) ? null : writeDeniedMessage(t, write, source)))
}
